import React, { useState, useEffect } from 'react'
import { Redirect } from '@reach/router'
import { useSelector } from 'react-redux'
import * as ReactBootstrap from 'react-bootstrap'
import '../styles.css'

import {TokenEntryForm} from './TokenEntryForm'
import { TokenSuccessDisplay, TokenErrorDisplay } from './TokenSuccessDisplay'
import NoTokenLocationSelected from '../NoTokenLocationSelected'
import * as rest from '../../../services'
import * as components from '../../../components'
import {RootState} from "../../../state/reducer";

// Static token routes (e.g. /token/qr, /token/display) are matched
// case-sensitively by @reach/router, so a wrong-case URL like /token/QR
// falls through to the token/:locationCode catch-all and wrongly renders
// the entry form. Redirect those reserved sub-paths to their canonical
// lowercase route instead.
const RESERVED_TOKEN_SUBPATHS: Record<string, string> = {
  qr: '/token/qr',
  display: '/token/display',
}

export const TokenEntryPage = (props) => {
  const { organization } = useSelector((state:RootState) => state.machineConfig)
  const { geoLocation } = useSelector((state:RootState) => state.application)
  const { locationCode } = props

  const [token, setToken] = useState(null)
  const [errors, setError] = useState('')
  const [inProgress, setInProgress] = useState(false)

  let intervalId = null

  const onTokenSave = (token) => {
    longPoll(token.url)
  }

  const onTokenSaveError = (err) => {
    if (err?.response?.data) {
      const data = err.response.data
      if (Array.isArray(data)) {
        setError(data[0])
      } else if (data.non_field_errors) {
        setError(data.non_field_errors[0])
      } else {
        setError('Could not create token. Please reach out to reception.')
      }
    }

    intervalId = setInterval(() => {
      setToken(null)
      setError('')
      clearInterval(intervalId)
    }, 10000)
  }

  const longPoll = (url, maxAttempts = 10, delay = 2000) => {
    let attempts = 0

    const poll = () => {
      if (attempts >= maxAttempts) {
        console.log('Max polling attempts reached.')
        return
      }

      fetch(url)
        .then((response) => response.json())
        .then((data) => {
          if (!isNaN(data.token_number)) {
            setInProgress(false)
            setToken(data)

            intervalId = setInterval(() => {
              setToken(null)
              setError('')
              clearInterval(intervalId)
            }, 10000)
            return
          }

          attempts++
          setTimeout(poll, delay)
        })
        .catch((error) => {
          console.error('Polling error:', error)
        })
    }

    poll()
  }

  const saveTokenInfo = (values) => {
    if(geoLocation){
        const payload = {
      ...values,
      ...geoLocation,
      location_code: organization?.token_machine_location_code || locationCode,
    }

    setInProgress(true)

    new rest.TokenService()
      .makePostRequest(payload)
      .then(onTokenSave)
      .catch((err) => {
        setInProgress(false)
        onTokenSaveError(err)
      })
    }

  }

  useEffect(() => {
    return () => {
      intervalId && clearInterval(intervalId)
    }
  }, [])

  const reservedTarget = locationCode
    ? RESERVED_TOKEN_SUBPATHS[locationCode.toLowerCase()]
    : undefined
  if (reservedTarget && locationCode !== reservedTarget.split('/').pop()) {
    return <Redirect to={reservedTarget} noThrow />
  }

  return (
    <div className="token-entry-page d-flex align-items-center justify-content-center">
      <ReactBootstrap.Container fluid className="px-3 py-4">
        <ReactBootstrap.Row className="justify-content-center">
          <ReactBootstrap.Col xs={12} sm={10} md={8} lg={6} xl={5}>
            {!organization && !locationCode && <NoTokenLocationSelected />}

            {token && !isNaN(token.token_number) && <TokenSuccessDisplay {...token} />}

            {errors && (
              <div className="mt-4">
                <TokenErrorDisplay message={errors} />
              </div>
            )}

            {(organization || locationCode) && !token && !errors && (
              <ReactBootstrap.Card className="shadow-sm">
                <ReactBootstrap.Card.Header as="h5" className="text-center">
                  {inProgress ? 'Please Wait!!!' : 'Just share a few quick details so we can generate your token.'}
                </ReactBootstrap.Card.Header>

                <ReactBootstrap.Card.Body className="p-4">
                  {inProgress ? <components.Loading /> : <TokenEntryForm onSubmit={saveTokenInfo} />}
                </ReactBootstrap.Card.Body>

                {organization && (
                  <ReactBootstrap.Alert className="mt-3 mx-3 mb-4" variant="info">
                    <strong className="text-dark">This Token will be valid only at:</strong>
                    <br />
                    {organization.name}
                    <br />
                    {organization.address}, {organization.city}, {organization.state}
                  </ReactBootstrap.Alert>
                )}
              </ReactBootstrap.Card>
            )}
          </ReactBootstrap.Col>
        </ReactBootstrap.Row>
      </ReactBootstrap.Container>
    </div>
  )
}
